import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatUserTime } from '../utils/timeFormat';
import { 
 Alert, Container, Paper, Typography, Button, Dialog, TextField, MenuItem, Select, FormControl,
 InputLabel, DialogTitle, DialogContent, DialogActions, IconButton, DialogContentText, InputAdornment,
 Box, Grid, Card, CardContent, Chip, Tabs, Tab
} from '@mui/material';
import { 
  Visibility, VisibilityOff, Dashboard as DashboardIcon,
  People, Security, AttachMoney, PersonAdd, Close, ContentCopy,
  TrendingUp, Analytics, Assessment, Group, Business,
  CollectionsBookmark, DataUsage, MonetizationOn, Search, Clear,
  Campaign, History, AccountCircle, Gavel
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { Archive, Unarchive, Delete } from '@mui/icons-material';
import axios from '../utils/axios';
import CustomerManagement from './CustomerManagement';
import AdminActivityDashboard from '../components/AdminActivityDashboard';
import AvatarsManagement from '../components/AvatarsManagement';
import ComplianceView from '../components/ComplianceView';

const AdminPanel = () => {
 const location = useLocation();
 const { user: currentUser } = useAuth();
 const [activeTab, setActiveTab] = useState(0);
 const [dashboardTab, setDashboardTab] = useState(0);
 const [dashboardData, setDashboardData] = useState(null);
 // User management with client-side caching
 const [allUsers, setAllUsers] = useState([]); // Full cached user data
 const [users, setUsers] = useState([]); // Filtered display data
 const [userSearch, setUserSearch] = useState(''); // Search term
 const [open, setOpen] = useState(false);
 const [showPassword, setShowPassword] = useState(false);
 const [error, setError] = useState(null);
 const [fieldErrors, setFieldErrors] = useState({});
 const [confirmDialog, setConfirmDialog] = useState({ 
   open: false, 
   type: null, 
   user: null,
   title: '',
   message: ''
 });
 const [newUser, setNewUser] = useState({
   email: '',
   password: '',
   first_name: '',
   last_name: '',
   role: 'user',
   user_type: 'Corporate',
   customer_id: ''
 });
 const [customers, setCustomers] = useState([]);
 const [corporateCustomers, setCorporateCustomers] = useState([]);
 
 // Check if current user is Super Admin
 const isSuperAdmin = currentUser?.role === 'super_admin';

 const getColumns = () => {
   const baseColumns = [
     { field: 'email', headerName: 'Email', width: 200 },
     { field: 'first_name', headerName: 'First Name', width: 130 },
     { field: 'last_name', headerName: 'Last Name', width: 130 },
     { field: 'role', headerName: 'Role', width: 100 },
     { 
       field: 'customer', 
       headerName: 'Company', 
       width: 150,
       renderCell: (params) => {
         const customer = customers.find(c => c.id === params.row.customer_id);
         return customer ? customer.company_name : (params.row.customer_id ? 'Unknown' : 'Personal');
       }
     },
     { field: 'is_active', headerName: 'Active', width: 100, type: 'boolean' },
     { 
       field: 'authType',
       headerName: 'Auth Type',
       width: 220,
       sortable: false,
       renderCell: (params) => {
         const authMethods = [];
         if (params.row.google_id) authMethods.push({ name: 'Google', color: 'error' });
         if (params.row.microsoft_id) authMethods.push({ name: 'Microsoft', color: 'primary' });
         if (params.row.password_hash) authMethods.push({ name: 'Password', color: 'success' });
         
         return (
           <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
             {authMethods.length > 0 ? (
               authMethods.map((method, index) => (
                 <Chip
                   key={method.name}
                   label={method.name}
                   color={method.color}
                   size="small"
                   variant="outlined"
                 />
               ))
             ) : (
               <Chip label="None" color="default" size="small" variant="outlined" />
             )}
           </Box>
         );
       }
     },
     {
       field: 'last_login',
       headerName: 'Last Login',
       width: 180,
       valueFormatter: (params) => 
         params.value ? formatUserTime(params.value, currentUser?.preferences) : '-'
     },
     {
       field: 'created_at',
       headerName: 'Created At',
       width: 180,
       valueFormatter: (params) =>
         formatUserTime(params.value, currentUser?.preferences)
     }
   ];

   // Only show actions column for Super Admin
   if (isSuperAdmin) {
     baseColumns.push({
       field: 'actions',
       headerName: 'Actions',
       width: 200,
       renderCell: (params) => (
         <>
           <IconButton 
             onClick={() => handleArchiveUser(params.row)}
             color={params.row.is_active ? "warning" : "success"}
           >
             {params.row.is_active ? <Archive /> : <Unarchive />}
           </IconButton>
           <IconButton 
             onClick={() => handleDeleteUser(params.row)}
             color="error"
           >
             <Delete />
           </IconButton>
         </>
       )
     });
   }

   return baseColumns;
 };

 const validateForm = () => {
   const errors = {};
   if (!newUser.email) errors.email = 'Email is required';
   if (!newUser.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) 
     errors.email = 'Invalid email format';
   
   // Domain validation for corporate users
   if (newUser.user_type === 'Corporate' && newUser.customer_id && newUser.email) {
     const selectedCustomer = corporateCustomers.find(c => c.id === newUser.customer_id);
     if (selectedCustomer) {
       const emailDomain = newUser.email.split('@')[1]?.toLowerCase();
       const companyDomain = selectedCustomer.domain.toLowerCase();
       if (emailDomain !== companyDomain) {
         errors.email = `Email must use company domain @${companyDomain}`;
       }
     }
   }
   
   if (!newUser.password) errors.password = 'Password is required';
   if (newUser.password.length < 8) 
     errors.password = 'Password must be at least 8 characters';
   if (newUser.password.length > 50) 
     errors.password = 'Password must be less than 50 characters';
   if (!newUser.first_name) errors.first_name = 'First name is required';
   if (newUser.first_name.length > 50) 
     errors.first_name = 'First name must be less than 50 characters';
   if (!newUser.last_name) errors.last_name = 'Last name is required';
   if (newUser.last_name.length > 50) 
     errors.last_name = 'Last name must be less than 50 characters';
   if (!newUser.role) errors.role = 'Role is required';
   if (!newUser.user_type) errors.user_type = 'User type is required';
   if (newUser.user_type === 'Corporate' && !newUser.customer_id) {
     errors.customer_id = 'Company is required for corporate users';
   }
   
   setFieldErrors(errors);
   return Object.keys(errors).length === 0;
 };

 const fetchUsers = async () => {
   try {
     const response = await axios.get('/admin/users');
     setAllUsers(response.data); // Cache all user data
   } catch (error) {
     setError(error.response?.data?.detail || 'Error fetching users');
   }
 };

 // Client-side user filtering
 const filterUsers = () => {
   if (!allUsers.length) {
     setUsers([]);
     return;
   }

   let filtered = allUsers;
   
   // Apply search filter
   if (userSearch.trim()) {
     const searchLower = userSearch.toLowerCase();
     filtered = filtered.filter(user => 
       (user.email && user.email.toLowerCase().includes(searchLower)) ||
       (user.first_name && user.first_name.toLowerCase().includes(searchLower)) ||
       (user.last_name && user.last_name.toLowerCase().includes(searchLower)) ||
       (user.role && user.role.toLowerCase().includes(searchLower)) ||
       (user.customer_id && user.customer_id.toLowerCase().includes(searchLower))
     );
   }

   setUsers(filtered);
 };

 const fetchCustomers = async () => {
   try {
     const [allCustomersResponse, corporateCustomersResponse] = await Promise.all([
       axios.get('/customers/customers'),
       axios.get('/customers/corporate')
     ]);
     setCustomers(allCustomersResponse.data);
     setCorporateCustomers(corporateCustomersResponse.data);
   } catch (error) {
     console.error('Error fetching customers:', error);
   }
 };

 const handleCreateUser = async () => {
   if (!validateForm()) return;
   try {
    // Normalize data before sending
    const normalizedUser = {
      ...newUser,
      email: newUser.email.toLowerCase(),
      first_name: newUser.first_name
        .trim()
        .toLowerCase()
        .replace(/\b\w/g, c => c.toUpperCase()),
      last_name: newUser.last_name
        .trim()
        .toLowerCase()
        .replace(/\b\w/g, c => c.toUpperCase())
    };
     await axios.post('/admin/users', normalizedUser);
     setOpen(false);
     fetchUsers();
     resetForm();
   } catch (error) {
     const details = error.response?.data?.detail;
     if (Array.isArray(details)) {
       const errors = {};
       details.forEach(err => errors[err.loc[1]] = err.msg);
       setFieldErrors(errors);
     } else {
       setError(details || 'Error creating user');
     }
   }
 };

 const handleArchiveUser = (user) => {
   setConfirmDialog({
     open: true,
     type: 'archive',
     user,
     title: `${user.is_active ? 'Archive' : 'Unarchive'} User`,
     message: `Are you sure you want to ${user.is_active ? 'archive' : 'unarchive'} ${user.first_name} ${user.last_name}?`
   });
 };

 const handleDeleteUser = (user) => {
   setConfirmDialog({
     open: true,
     type: 'delete',
     user,
     title: 'Delete User',
     message: `Are you sure you want to permanently delete ${user.first_name} ${user.last_name}? This action cannot be undone.`
   });
 };

  const resetForm = () => {
    setNewUser({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      role: 'user',
      user_type: 'Corporate',
      customer_id: ''
    });
    setFieldErrors({});
    setError(null);
  };

 const handleConfirmDialog = async () => {
   try {
     if (confirmDialog.type === 'archive') {
       await axios.patch(`/admin/users/${confirmDialog.user._id}/archive`);
     } else {
       await axios.delete(`/admin/users/${confirmDialog.user._id}`);
     }
     await fetchUsers();
   } catch (error) {
     setError(`Failed to ${confirmDialog.type} user`);
   }
   setConfirmDialog({ open: false, type: null, user: null });
 };

 const fetchDashboard = async () => {
   try {
     const response = await axios.get('/admin/dashboard');
     setDashboardData(response.data);
   } catch (error) {
     console.error('Failed to fetch dashboard data:', error);
   }
 };

 useEffect(() => {
   fetchUsers();
   fetchDashboard();
   fetchCustomers();
 }, []);

 // Client-side filtering for users
 useEffect(() => {
   filterUsers();
 }, [allUsers, userSearch]);

 useEffect(() => {
   const params = new URLSearchParams(location.search);
   const tab = params.get('tab');
   
   if (tab === 'dashboard') {
     setActiveTab(0);
   } else if (tab === 'users') {
     setActiveTab(1);
   } else if (tab === 'customers') {
     setActiveTab(2);
   } else if (tab === 'avatars') {
     setActiveTab(3);
   } else if (tab === 'audit') {
     setActiveTab(4);
   } else if (tab === 'compliance') {
     setActiveTab(5);
   } else {
     setActiveTab(0); // default to dashboard
   }
 }, [location.search]);


 const renderDashboard = () => {
   if (!dashboardData) return <Typography>Loading dashboard...</Typography>;

   return (
     <Box>
       <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
         <Tabs value={dashboardTab} onChange={(e, newValue) => setDashboardTab(newValue)}>
           <Tab icon={<Group />} label="Customers" />
           <Tab icon={<CollectionsBookmark />} label="Collection Status" />
           <Tab icon={<MonetizationOn />} label="Financials" />
         </Tabs>
       </Box>

       {dashboardTab === 0 && renderCustomersTab()}
       {dashboardTab === 1 && renderCollectionTab()}
       {dashboardTab === 2 && renderFinancialsTab()}
     </Box>
   );
 };

 const renderCustomersTab = () => {
   const data = dashboardData?.customers || {};
   return (
     <Box>
       <Typography variant="h6" gutterBottom>Customer & User Metrics</Typography>
       <Grid container spacing={3} sx={{ mb: 4 }}>
         <Grid item xs={12} sm={6} md={3}>
           <Card>
             <CardContent>
               <Box sx={{ display: 'flex', alignItems: 'center' }}>
                 <Business sx={{ mr: 2, color: 'primary.main', fontSize: 40 }} />
                 <Box>
                   <Typography color="textSecondary" gutterBottom>Total Customers</Typography>
                   <Typography variant="h4">{data.total_customers || 0}</Typography>
                 </Box>
               </Box>
             </CardContent>
           </Card>
         </Grid>
         
         <Grid item xs={12} sm={6} md={3}>
           <Card>
             <CardContent>
               <Box sx={{ display: 'flex', alignItems: 'center' }}>
                 <People sx={{ mr: 2, color: 'success.main', fontSize: 40 }} />
                 <Box>
                   <Typography color="textSecondary" gutterBottom>Monthly Active Users</Typography>
                   <Typography variant="h4">{data.monthly_active_users || 0}</Typography>
                 </Box>
               </Box>
             </CardContent>
           </Card>
         </Grid>

         <Grid item xs={12} sm={6} md={3}>
           <Card>
             <CardContent>
               <Box sx={{ display: 'flex', alignItems: 'center' }}>
                 <PersonAdd sx={{ mr: 2, color: 'info.main', fontSize: 40 }} />
                 <Box>
                   <Typography color="textSecondary" gutterBottom>Daily Active Users</Typography>
                   <Typography variant="h4">{data.daily_active_users || 0}</Typography>
                 </Box>
               </Box>
             </CardContent>
           </Card>
         </Grid>

         <Grid item xs={12} sm={6} md={3}>
           <Card>
             <CardContent>
               <Box sx={{ display: 'flex', alignItems: 'center' }}>
                 <AttachMoney sx={{ mr: 2, color: 'warning.main', fontSize: 40 }} />
                 <Box>
                   <Typography color="textSecondary" gutterBottom>Premium Users</Typography>
                   <Typography variant="h4">{data.premium_users || 0}</Typography>
                 </Box>
               </Box>
             </CardContent>
           </Card>
         </Grid>
       </Grid>

       {/* Customer Distribution Chart */}
       <Paper sx={{ p: 3, mb: 3 }}>
         <Typography variant="h6" gutterBottom>Customer Distribution</Typography>
         <Grid container spacing={4} alignItems="center">
           <Grid item xs={12} md={8}>
             <Box sx={{ mt: 2 }}>
               <Box sx={{ mb: 2 }}>
                 <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                   <Typography variant="body2" color="textSecondary">Corporate Customers</Typography>
                   <Typography variant="body2" fontWeight="bold">{data.corporate_customers || 0}</Typography>
                 </Box>
                 <Box sx={{ height: 20, backgroundColor: '#e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                   <Box sx={{
                     height: '100%', backgroundColor: 'primary.main',
                     width: `${data.total_customers > 0 ? ((data.corporate_customers || 0) / data.total_customers * 100) : 0}%`,
                     transition: 'width 0.3s ease'
                   }} />
                 </Box>
               </Box>
               <Box>
                 <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                   <Typography variant="body2" color="textSecondary">Personal Customers</Typography>
                   <Typography variant="body2" fontWeight="bold">{data.personal_customers || 0}</Typography>
                 </Box>
                 <Box sx={{ height: 20, backgroundColor: '#e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                   <Box sx={{
                     height: '100%', backgroundColor: 'secondary.main',
                     width: `${data.total_customers > 0 ? ((data.personal_customers || 0) / data.total_customers * 100) : 0}%`,
                     transition: 'width 0.3s ease'
                   }} />
                 </Box>
               </Box>
             </Box>
           </Grid>
           <Grid item xs={12} md={4}>
             <Box sx={{ textAlign: 'center' }}>
               <Typography variant="h3" color="primary.main" gutterBottom>{data.total_customers || 0}</Typography>
               <Typography variant="h6" color="textSecondary" gutterBottom>Total Customers</Typography>
               <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 2 }}>
                 <Box>
                   <Chip 
                     label={`${data.total_customers > 0 ? Math.round((data.corporate_customers || 0) / data.total_customers * 100) : 0}%`} 
                     color="primary" variant="outlined" size="small"
                   />
                   <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>Corporate</Typography>
                 </Box>
                 <Box>
                   <Chip 
                     label={`${data.total_customers > 0 ? Math.round((data.personal_customers || 0) / data.total_customers * 100) : 0}%`} 
                     color="secondary" variant="outlined" size="small"
                   />
                   <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>Personal</Typography>
                 </Box>
               </Box>
             </Box>
           </Grid>
         </Grid>
       </Paper>

       {/* Additional Customer Metrics */}
       <Grid container spacing={3}>
         <Grid item xs={12} md={6}>
           <Paper sx={{ p: 3 }}>
             <Typography variant="h6" gutterBottom>User Activity</Typography>
             <Grid container spacing={2}>
               <Grid item xs={6}>
                 <Box sx={{ textAlign: 'center', p: 2 }}>
                   <Typography variant="h4" color="primary">{data.total_users || 0}</Typography>
                   <Typography variant="body2" color="textSecondary">Total Users</Typography>
                 </Box>
               </Grid>
               <Grid item xs={6}>
                 <Box sx={{ textAlign: 'center', p: 2 }}>
                   <Typography variant="h4" color="success.main">{data.active_users || 0}</Typography>
                   <Typography variant="body2" color="textSecondary">Active Users</Typography>
                 </Box>
               </Grid>
               <Grid item xs={6}>
                 <Box sx={{ textAlign: 'center', p: 2 }}>
                   <Typography variant="h4" color="warning.main">{data.mfa_users || 0}</Typography>
                   <Typography variant="body2" color="textSecondary">MFA Enabled</Typography>
                 </Box>
               </Grid>
               <Grid item xs={6}>
                 <Box sx={{ textAlign: 'center', p: 2 }}>
                   <Typography variant="h4" color="info.main">{Math.round((data.monthly_active_users || 0) / Math.max(data.total_users, 1) * 100)}%</Typography>
                   <Typography variant="body2" color="textSecondary">Monthly Engagement</Typography>
                 </Box>
               </Grid>
             </Grid>
           </Paper>
         </Grid>
         <Grid item xs={12} md={6}>
           <Paper sx={{ p: 3 }}>
             <Typography variant="h6" gutterBottom>Business Metrics (Coming Soon)</Typography>
             <Grid container spacing={2}>
               <Grid item xs={6}>
                 <Box sx={{ textAlign: 'center', p: 2 }}>
                   <Typography variant="h4" color="textSecondary">-</Typography>
                   <Typography variant="body2" color="textSecondary">Customer Acquisition Rate</Typography>
                 </Box>
               </Grid>
               <Grid item xs={6}>
                 <Box sx={{ textAlign: 'center', p: 2 }}>
                   <Typography variant="h4" color="textSecondary">-</Typography>
                   <Typography variant="body2" color="textSecondary">Retention Rate</Typography>
                 </Box>
               </Grid>
               <Grid item xs={6}>
                 <Box sx={{ textAlign: 'center', p: 2 }}>
                   <Typography variant="h4" color="textSecondary">-</Typography>
                   <Typography variant="body2" color="textSecondary">Churn Rate</Typography>
                 </Box>
               </Grid>
               <Grid item xs={6}>
                 <Box sx={{ textAlign: 'center', p: 2 }}>
                   <Typography variant="h4" color="textSecondary">-</Typography>
                   <Typography variant="body2" color="textSecondary">NPS Score</Typography>
                 </Box>
               </Grid>
             </Grid>
           </Paper>
         </Grid>
       </Grid>
     </Box>
   );
 };

 const renderCollectionTab = () => {
   const data = dashboardData?.collection || {};
   return (
     <Box>
       <Typography variant="h6" gutterBottom>Collection Status & Platform Analytics</Typography>
       <Grid container spacing={3} sx={{ mb: 4 }}>
         <Grid item xs={12} sm={6} md={3}>
           <Card>
             <CardContent>
               <Box sx={{ display: 'flex', alignItems: 'center' }}>
                 <CollectionsBookmark sx={{ mr: 2, color: 'primary.main', fontSize: 40 }} />
                 <Box>
                   <Typography color="textSecondary" gutterBottom>Total Collectors</Typography>
                   <Typography variant="h4">{data.total_collectors || 0}</Typography>
                 </Box>
               </Box>
             </CardContent>
           </Card>
         </Grid>
         
         <Grid item xs={12} sm={6} md={3}>
           <Card>
             <CardContent>
               <Box sx={{ display: 'flex', alignItems: 'center' }}>
                 <DataUsage sx={{ mr: 2, color: 'success.main', fontSize: 40 }} />
                 <Box>
                   <Typography color="textSecondary" gutterBottom>Total Avatars</Typography>
                   <Typography variant="h4">{data.total_avatars || 0}</Typography>
                 </Box>
               </Box>
             </CardContent>
           </Card>
         </Grid>

         <Grid item xs={12} sm={6} md={3}>
           <Card>
             <CardContent>
               <Box sx={{ display: 'flex', alignItems: 'center' }}>
                 <TrendingUp sx={{ mr: 2, color: 'info.main', fontSize: 40 }} />
                 <Box>
                   <Typography color="textSecondary" gutterBottom>Success Rate</Typography>
                   <Typography variant="h4">{Math.round((data.collection_success_rate || 0) * 100)}%</Typography>
                 </Box>
               </Box>
             </CardContent>
           </Card>
         </Grid>

         <Grid item xs={12} sm={6} md={3}>
           <Card>
             <CardContent>
               <Box sx={{ display: 'flex', alignItems: 'center' }}>
                 <Analytics sx={{ mr: 2, color: 'warning.main', fontSize: 40 }} />
                 <Box>
                   <Typography color="textSecondary" gutterBottom>Avg Collections</Typography>
                   <Typography variant="h4">{Math.round(data.average_collections_per_collector || 0)}</Typography>
                 </Box>
               </Box>
             </CardContent>
           </Card>
         </Grid>
       </Grid>

       <Paper sx={{ p: 3, mb: 3 }}>
         <Typography variant="h6" gutterBottom>Platform Distribution</Typography>
         <Grid container spacing={2}>
           {Object.entries(data.platform_distribution || {}).map(([platform, count]) => (
             <Grid item xs={6} md={2} key={platform}>
               <Box sx={{ textAlign: 'center', p: 2 }}>
                 <Typography variant="h5" color="primary">{count}</Typography>
                 <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>{platform}</Typography>
               </Box>
             </Grid>
           ))}
         </Grid>
       </Paper>

       <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
         Collection system metrics will be populated once the collector infrastructure is implemented.
       </Typography>
     </Box>
   );
 };

 const renderFinancialsTab = () => {
   const data = dashboardData?.financials || {};
   return (
     <Box>
       <Typography variant="h6" gutterBottom>Financial Performance</Typography>
       <Grid container spacing={3} sx={{ mb: 4 }}>
         <Grid item xs={12} sm={6} md={3}>
           <Card>
             <CardContent>
               <Box sx={{ display: 'flex', alignItems: 'center' }}>
                 <MonetizationOn sx={{ mr: 2, color: 'success.main', fontSize: 40 }} />
                 <Box>
                   <Typography color="textSecondary" gutterBottom>Monthly Revenue</Typography>
                   <Typography variant="h4">{"$" + (data.monthly_recurring_revenue || 0).toLocaleString()}</Typography>
                 </Box>
               </Box>
             </CardContent>
           </Card>
         </Grid>
         
         <Grid item xs={12} sm={6} md={3}>
           <Card>
             <CardContent>
               <Box sx={{ display: 'flex', alignItems: 'center' }}>
                 <Assessment sx={{ mr: 2, color: 'primary.main', fontSize: 40 }} />
                 <Box>
                   <Typography color="textSecondary" gutterBottom>Annual Revenue</Typography>
                   <Typography variant="h4">{"$" + (data.annual_recurring_revenue || 0).toLocaleString()}</Typography>
                 </Box>
               </Box>
             </CardContent>
           </Card>
         </Grid>

         <Grid item xs={12} sm={6} md={3}>
           <Card>
             <CardContent>
               <Box sx={{ display: 'flex', alignItems: 'center' }}>
                 <TrendingUp sx={{ mr: 2, color: 'info.main', fontSize: 40 }} />
                 <Box>
                   <Typography color="textSecondary" gutterBottom>ARPU</Typography>
                   <Typography variant="h4">{"$" + Math.round(data.average_revenue_per_user || 0)}</Typography>
                 </Box>
               </Box>
             </CardContent>
           </Card>
         </Grid>

         <Grid item xs={12} sm={6} md={3}>
           <Card>
             <CardContent>
               <Box sx={{ display: 'flex', alignItems: 'center' }}>
                 <Analytics sx={{ mr: 2, color: 'warning.main', fontSize: 40 }} />
                 <Box>
                   <Typography color="textSecondary" gutterBottom>Conversion Rate</Typography>
                   <Typography variant="h4">{Math.round((data.conversion_rate || 0) * 100)}%</Typography>
                 </Box>
               </Box>
             </CardContent>
           </Card>
         </Grid>
       </Grid>

       <Grid container spacing={3}>
         <Grid item xs={12} md={6}>
           <Paper sx={{ p: 3 }}>
             <Typography variant="h6" gutterBottom>Revenue by Plan</Typography>
             <Box sx={{ mt: 2 }}>
               {Object.entries(data.revenue_by_plan || {}).map(([plan, revenue]) => (
                 <Box key={plan} sx={{ mb: 2 }}>
                   <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                     <Typography variant="body2" color="textSecondary" sx={{ textTransform: 'capitalize' }}>
                       {plan} Plan
                     </Typography>
                     <Typography variant="body2" fontWeight="bold">
                       {"$" + revenue.toLocaleString()}
                     </Typography>
                   </Box>
                   <Box sx={{ height: 8, backgroundColor: '#e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                     <Box sx={{
                       height: '100%', 
                       backgroundColor: plan === 'enterprise' ? 'warning.main' : plan === 'premium' ? 'primary.main' : 'secondary.main',
                       width: `${data.total_revenue > 0 ? (revenue / data.total_revenue * 100) : 0}%`,
                       transition: 'width 0.3s ease'
                     }} />
                   </Box>
                 </Box>
               ))}
             </Box>
           </Paper>
         </Grid>
         <Grid item xs={12} md={6}>
           <Paper sx={{ p: 3 }}>
             <Typography variant="h6" gutterBottom>Financial Health</Typography>
             <Grid container spacing={2}>
               <Grid item xs={6}>
                 <Box sx={{ textAlign: 'center', p: 2 }}>
                   <Typography variant="h4" color="success.main">{Math.round((data.payment_success_rate || 0) * 100)}%</Typography>
                   <Typography variant="body2" color="textSecondary">Payment Success</Typography>
                 </Box>
               </Grid>
               <Grid item xs={6}>
                 <Box sx={{ textAlign: 'center', p: 2 }}>
                   <Typography variant="h4" color="error">{Math.round((data.chargeback_rate || 0) * 100)}%</Typography>
                   <Typography variant="body2" color="textSecondary">Chargeback Rate</Typography>
                 </Box>
               </Grid>
               <Grid item xs={6}>
                 <Box sx={{ textAlign: 'center', p: 2 }}>
                   <Typography variant="h4" color="warning.main">{Math.round((data.refund_rate || 0) * 100)}%</Typography>
                   <Typography variant="body2" color="textSecondary">Refund Rate</Typography>
                 </Box>
               </Grid>
               <Grid item xs={6}>
                 <Box sx={{ textAlign: 'center', p: 2 }}>
                   <Typography variant="h4" color="info.main">{data.outstanding_invoices || 0}</Typography>
                   <Typography variant="body2" color="textSecondary">Outstanding Invoices</Typography>
                 </Box>
               </Grid>
             </Grid>
           </Paper>
         </Grid>
       </Grid>
       
       <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
         Financial metrics will be populated once billing system integration is complete.
       </Typography>
     </Box>
   );
 };

 return (
   <>
     <Container maxWidth="lg" sx={{ mt: 4 }}>
       <Paper sx={{ p: 3 }}>
         <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
           <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
             <Tab icon={<DashboardIcon />} label="Admin Dashboard" />
             <Tab icon={<People />} label="User Management" />
             <Tab icon={<Security />} label="Customer Management" />
             <Tab icon={<AccountCircle />} label="Avatars Management" />
             <Tab icon={<History />} label="Audit Trail" />
             <Tab icon={<Gavel />} label="Compliance" />
           </Tabs>
         </Box>

         {activeTab === 0 && renderDashboard()}
         {activeTab === 1 && (
           <Box>
             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
               <Typography variant="h5">User Management</Typography>
               {isSuperAdmin && (
                 <Button
                   variant="contained"
                   startIcon={<PersonAdd />}
                   onClick={() => setOpen(true)}
                 >
                   Create User
                 </Button>
               )}
             </Box>
             <Box sx={{ mb: 2 }}>
               <TextField
                 size="small"
                 placeholder="Search users..."
                 value={userSearch}
                 onChange={(e) => setUserSearch(e.target.value)}
                 InputProps={{
                   startAdornment: (
                     <InputAdornment position="start">
                       <Search />
                     </InputAdornment>
                   ),
                   endAdornment: userSearch && (
                     <InputAdornment position="end">
                       <IconButton
                         size="small"
                         onClick={() => setUserSearch('')}
                         edge="end"
                       >
                         <Clear />
                       </IconButton>
                     </InputAdornment>
                   )
                 }}
                 sx={{ minWidth: 300 }}
               />
             </Box>
             {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
             <Paper sx={{ width: '100%' }}>
               <DataGrid
                 rows={users}
                 columns={getColumns()}
                 pageSize={10}
                 autoHeight
                 getRowId={(row) => row._id}
               />
             </Paper>
           </Box>
         )}
         {activeTab === 2 && (
           <CustomerManagement />
         )}
         {activeTab === 3 && (
           <AvatarsManagement />
         )}
         {activeTab === 4 && (
           <AdminActivityDashboard />
         )}
         {activeTab === 5 && (
           <ComplianceView />
         )}
       </Paper>
     </Container>

     <Dialog 
       open={open} 
       onClose={(event, reason) => {
         if (reason === 'escapeKeyDown') {
           setOpen(false);
           resetForm();
         }
         // Prevent closing on backdrop click
       }}
       disableEnforceFocus 
       disablePortal 
       keepMounted={false}
       disableEscapeKeyDown={false}
       maxWidth="sm"
       fullWidth
       PaperProps={{
         sx: {
           minHeight: '60vh',
           maxHeight: '90vh',
           display: 'flex',
           flexDirection: 'column'
         }
       }}
     >
       <DialogTitle sx={{ 
         display: 'flex', 
         justifyContent: 'space-between', 
         alignItems: 'center',
         pr: 1
       }}>
         Create New User
         <IconButton
           aria-label="close"
           onClick={() => {
             setOpen(false);
             resetForm();
           }}
           sx={{ color: 'text.secondary' }}
         >
           <Close />
         </IconButton>
       </DialogTitle>
       <DialogContent sx={{ flex: 1, overflowY: 'auto', pb: 1 }}>
         {error && (
           <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
             {String(error)}
           </Alert>
         )}
         <Grid container spacing={2} sx={{ mt: 1 }}>
           {/* User Information */}
           <Grid item xs={12}>
             <Typography variant="h6" gutterBottom>User Information</Typography>
           </Grid>
           <Grid item xs={12}>
             <FormControl fullWidth margin="normal">
               <InputLabel>User Type</InputLabel>
               <Select
                 value={newUser.user_type}
                 label="User Type"
                 onChange={(e) => {
                   setNewUser({
                     ...newUser, 
                     user_type: e.target.value,
                     customer_id: e.target.value === 'Personal' ? '' : newUser.customer_id,
                     email: '' // Clear email when user type changes
                   });
                 }}
                 error={!!fieldErrors.user_type}
                 sx={{
                   '& .MuiSelect-select': {
                     color: 'text.primary',
                   },
                   '& .MuiMenuItem-root': {
                     color: 'text.primary',
                   }
                 }}
               >
                 <MenuItem value="Corporate">Corporate</MenuItem>
                 <MenuItem value="Personal">Personal</MenuItem>
               </Select>
             </FormControl>
           </Grid>

           {newUser.user_type === 'Corporate' && (
             <Grid item xs={12}>
               <FormControl fullWidth margin="normal">
                 <InputLabel>Company</InputLabel>
                 <Select
                   value={newUser.customer_id}
                   label="Company"
                   onChange={(e) => {
                     setNewUser({
                       ...newUser, 
                       customer_id: e.target.value,
                       email: '' // Clear email when company changes
                     });
                   }}
                   error={!!fieldErrors.customer_id}
                   sx={{
                     '& .MuiSelect-select': {
                       color: 'text.primary',
                     },
                     '& .MuiMenuItem-root': {
                       color: 'text.primary',
                     }
                   }}
                 >
                   {corporateCustomers.map((customer) => (
                     <MenuItem 
                       key={customer.id} 
                       value={customer.id}
                       sx={{ 
                         color: 'text.primary',
                         '&:hover': {
                           backgroundColor: 'action.hover'
                         }
                       }}
                     >
                       <Box>
                         <Typography variant="body1" sx={{ fontWeight: 500 }}>
                           {customer.company_name}
                         </Typography>
                         <Typography variant="body2" color="text.secondary">
                           {customer.domain}
                         </Typography>
                       </Box>
                     </MenuItem>
                   ))}
                 </Select>
                 {fieldErrors.customer_id && (
                   <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                     {fieldErrors.customer_id}
                   </Typography>
                 )}
               </FormControl>
             </Grid>
           )}

           <Grid item xs={6}>
             <TextField
               required
               fullWidth
               label="First Name"
               value={newUser.first_name}
               onChange={(e) => setNewUser({...newUser, first_name: e.target.value})}
               error={!!fieldErrors.first_name}
               helperText={fieldErrors.first_name}
               margin="normal"
               inputProps={{ maxLength: 50 }}
             />
           </Grid>
           <Grid item xs={6}>
             <TextField
               required
               fullWidth
               label="Last Name"
               value={newUser.last_name}
               onChange={(e) => setNewUser({...newUser, last_name: e.target.value})}
               error={!!fieldErrors.last_name}
               helperText={fieldErrors.last_name}
               margin="normal"
               inputProps={{ maxLength: 50 }}
             />
           </Grid>
           <Grid item xs={12}>
             <TextField
               fullWidth
               required
               label="Email"
               value={newUser.email}
               onChange={(e) => setNewUser({...newUser, email: e.target.value})}
               error={!!fieldErrors.email}
               helperText={fieldErrors.email || (newUser.user_type === 'Corporate' && newUser.customer_id ? 
                 `Must use @${corporateCustomers.find(c => c.id === newUser.customer_id)?.domain}` : '')}
               margin="normal"
               placeholder={newUser.user_type === 'Corporate' && newUser.customer_id ? 
                 `user@${corporateCustomers.find(c => c.id === newUser.customer_id)?.domain}` : 'user@example.com'}
             />
           </Grid>
           <Grid item xs={12}>
             <TextField
              fullWidth
              required
              label="Password"
              type={showPassword ? "text" : "password"}
              value={newUser.password}
              onChange={(e) => setNewUser({...newUser, password: e.target.value})}
              error={!!fieldErrors.password}
              helperText={fieldErrors.password}
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
           </Grid>
           <Grid item xs={12}>
             <FormControl fullWidth margin="normal">
               <InputLabel>Role</InputLabel>
               <Select
                 value={newUser.role}
                 label="Role"
                 onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                 error={!!fieldErrors.role}
               >
                 <MenuItem value="user">User</MenuItem>
                 <MenuItem value="admin">Admin</MenuItem>
                 <MenuItem value="super_admin">Super Admin</MenuItem>
                 <MenuItem value="manager">Manager</MenuItem>
               </Select>
             </FormControl>
           </Grid>
         </Grid>
       </DialogContent>
       <DialogActions sx={{ p: 2 }}>
         <Button onClick={() => { setOpen(false); resetForm(); }}>
           Cancel
         </Button>
         <Button variant="contained" onClick={handleCreateUser}>
           Create User
         </Button>
       </DialogActions>
     </Dialog>

     <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false })} disableEnforceFocus>
       <DialogTitle>{confirmDialog.title}</DialogTitle>
       <DialogContent>
         <DialogContentText>{confirmDialog.message}</DialogContentText>
       </DialogContent>
       <DialogActions>
         <Button onClick={() => setConfirmDialog({ open: false })}>Cancel</Button>
         <Button onClick={handleConfirmDialog} color={confirmDialog.type === 'delete' ? 'error' : 'warning'} autoFocus>
           Confirm
         </Button>
       </DialogActions>
     </Dialog>
   </>
 );
};

export default AdminPanel;